/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toOpcoCharge } from '@data/dto/opco-charge.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  OpcoCharge,
  OpcoChargeHttp,
  OpcoChargeWizard,
} from '@data/models/opco-charge.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class OpcoChargeService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly opcoChargeEndpoint = '/opco-charge-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listOpcoCharges(
    filters: FilterParams,
  ): Promise<PaginateResult<OpcoCharge[]>> {
    const { data: response, error } = await this.API?.get<{
      data: OpcoChargeHttp[];
      meta: Meta;
    }>(this.opcoChargeEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toOpcoCharge) || [],
      totalSize: response?.meta?.page.total || 0,
    };
  }

  public async upsertOpcoCharge({
    id,
    ...opcoChargeWizard
  }: OpcoChargeWizard): Promise<OpcoCharge | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<OpcoChargeWizard, 'id'> },
        { data: OpcoChargeHttp }
      >(this.opcoChargeEndpoint, { data: opcoChargeWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toOpcoCharge(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: OpcoChargeWizard },
      { data: OpcoChargeHttp }
    >(`${this.opcoChargeEndpoint}/${id}`, {
      data: { ...opcoChargeWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toOpcoCharge(data?.data || {});
  }

  public async deleteOpcoCharge(id: string): Promise<OpcoCharge | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: OpcoChargeHttp;
    }>(`${this.opcoChargeEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toOpcoCharge(data?.data || {});
  }

  public async sortOpcoCharge(
    sortParams: DatatableReorderParams<OpcoCharge>,
  ): Promise<OpcoCharge | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.opcoChargeEndpoint}/${id}/sort`,
      {
        data: toSortParams('opco_charge_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
